import cx from 'classnames'
import dayjs from 'dayjs'

import Styles from './AutopilotDetails.module.scss'

import { useVirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useVirtualMarketerPlan'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

import { PaymentDetails } from '../PaymentDetails/PaymentDetails'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ManageVirtualMarketerPlanModal } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/ManageVirtualMarketerPlanModal/ManageVirtualMarketerPlanModal'
import { FetchSubscriptionResponse } from 'api/clients/WorkspaceClient'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { useCampaignsFeatureFlag } from 'hooks/useCampaignsFeatureFlag'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { AutopilotDetailsV2 } from './AutopilotDetailsV2'
import { useRootStore } from 'stores/RootStore'
import { useIsActiveOrCanceledPioneerPlan } from 'hooks/useIsActiveOrCanceledPioneerPlan'
import { usePioneerCredits } from 'hooks/usePioneerCredits'

export const AutopilotDetails = ({ currentSubscription }: { currentSubscription: FetchSubscriptionResponse }) => {
  const workspace = useCurrentWorkspace()
  const isCampaignsEnabled = useCampaignsFeatureFlag()
  const { creditUsage, loading } = useRootStore().creditUsageStore
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const { data: virtualMarketerPlan } = useVirtualMarketerPlan()
  const { openModal } = useModals()
  const { prices, trial_days_left, next_payment, plan, status, cancel_at, card } = currentSubscription
  const price = prices?.[0]
  const isActiveOrCanceledPioneerPlan = useIsActiveOrCanceledPioneerPlan()
  const isPioneerCreditsEnabled = usePioneerCredits()

  const openPlansModal = () => {
    if (isCampaignsEnabled || isActiveOrCanceledPioneerPlan) {
      openAutopilotPlansModal()
    } else {
      openVirtualMarketPlansModal()
    }
  }

  const openAutopilotPlansModal = () => {
    openModal(AutopilotPlansModal, {}, { newStack: true })
  }

  const openVirtualMarketPlansModal = () => {
    if (!virtualMarketerPlan || !price) return

    openModal(
      ManageVirtualMarketerPlanModal,
      {
        plan: virtualMarketerPlan,
        currentPlanIntervalCount: price.recurring.interval_count,
      },
      {
        newStack: true,
      },
    )
  }

  const getBillingPeriodText = () => {
    if (!price) return 'Billing plan'

    if (isCampaignsEnabled) {
      if (workspace?.is_ap_freemium) {
        return 'Free plan'
      }

      return `${nameMap[plan]} plan / ${
        price.recurring.interval_count === 1 ? 'Monthly' : `${price.recurring.interval_count} months`
      }`
    } else {
      return `${price.recurring.interval_count === 1 ? 'Month to month' : `${price.recurring.interval_count} months`}`
    }
  }

  const managePlanButtonText = () => {
    if (workspace?.paying) {
      if (isCampaignsEnabled) {
        return 'Manage Plan'
      } else {
        return 'Manage Subscription'
      }
    }

    return 'Subscribe'
  }

  const managePlanButtonVariant = () => {
    if (isCampaignsEnabled) {
      return plan === PaymentPlanKey.VirtualMarketer ? 'secondary' : 'primary'
    }

    return 'primary'
  }

  const textColor = isCampaignsEnabled ? 'var(--light-100)' : 'var(--Purple-70, #2E3A9C)'

  if (loading) {
    return null
  }

  if ((creditUsage && Object.keys(creditUsage).length > 0) || isPioneerCreditsEnabled) {
    return <AutopilotDetailsV2 />
  }

  return (
    <div>
      {isSettingsModalUpdateEnabled ? (
        <Modal.Header title="Billing" className={Styles.newHeader} />
      ) : (
        <div className={Styles.header}>
          <Heading level={3}>Billing</Heading>
        </div>
      )}
      <div
        className={cx(Styles.details, Styles[workspace?.is_ap_freemium ? PaymentPlanKey.Free : plan], {
          [Styles.autopilotPriceEnabled]: isCampaignsEnabled,
        })}
      >
        <div className={Styles.planDetails}>
          <div className={Styles.message}>
            <Heading level={3} color={textColor}>
              {getBillingPeriodText()}
            </Heading>
            {!workspace?.is_ap_freemium && (
              <div className={Styles.nextPaymentPill}>
                {(trial_days_left || 0) > 0 && (
                  <>
                    <Text color={textColor} variant="smallList">
                      {trial_days_left} days left
                    </Text>
                  </>
                )}
                {next_payment?.due_date && !cancel_at && (
                  <>
                    {(trial_days_left || 0) > 0 && <span> - </span>}
                    <Text color={textColor} variant="smallList">
                      {trial_days_left ? 'n' : 'N'}ext payment: {dayjs(next_payment.due_date).format('MMM D, YYYY')}
                    </Text>
                  </>
                )}
                {status === 'canceled' && (
                  <Text color={textColor} variant="smallList">
                    Canceled
                  </Text>
                )}
                {cancel_at && (
                  <Text color={textColor} variant="smallList">
                    {status === 'trialing' ? 'Expires' : 'Cancels'} at {dayjs(cancel_at).format('MMM D, YYYY')}
                  </Text>
                )}
              </div>
            )}
          </div>
          {isCampaignsEnabled && cancel_at ? (
            <Text color={textColor} variant="smallList">
              Your Free plan is scheduled to start on {dayjs(cancel_at).format('MMM D, YYYY')}
            </Text>
          ) : (
            <>
              {!workspace?.is_ap_freemium ? (
                <Text color={textColor} variant="smallList">
                  {price ? `$${price.unit_amount / 100} USD` : 'Price unavailable'}
                </Text>
              ) : (
                <Text color={textColor} variant="smallList">
                  $0 USD
                </Text>
              )}
            </>
          )}
        </div>
        <div
          className={cx({
            [Styles.managePlanButtonContainer]: plan === PaymentPlanKey.VirtualMarketer,
          })}
        >
          <Button onPress={openPlansModal} variant={managePlanButtonVariant()}>
            {managePlanButtonText()}
          </Button>
        </div>
      </div>
      <br />
      <br />
      <Heading level={5}>Payment Details</Heading>
      <div>{<PaymentDetails lastFour={card?.last_four} />}</div>
      <div className={Styles.horizontalRuler} />
      <Heading level={5}>Questions</Heading>
      <p className={Styles.questionCopy}>
        Contact us anytime at <a href="mailto:support@blaze.ai">support@blaze.ai</a> or hit the question mark at the
        bottom right corner and chat to us via Intercom.
      </p>
    </div>
  )
}
